var __importDefault = (this && this.__importDefault) || function (mod) {
  return (mod && mod.__esModule) ? mod : { "default": mod };
};
/**
 * EventDetailModal — Shows full event details in a centered white modal.
 * Renders different field sets per event type (Project, NonProject, Holiday,
 * Outlook) matching the original ce-calendar.html detail panel.
 */
import React, { memo, useEffect, useMemo, useRef } from 'react';
import { createFocusTrap } from '../../utils/accessibilityUtils';
import { format } from 'date-fns';
import { appSettings } from '../../../../../environments/common/appsettingsGenerator';
/* ---- Styles ---- */
const s = {
    backdrop: {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 10001,
    },
    panel: {
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: '450px', maxWidth: '100vw',
        backgroundColor: '#fff',
        boxShadow: '-4px 0 24px rgba(0,0,0,0.18)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        zIndex: 10002,
        animation: 'slideInFromRight 0.25s ease-out',
    },
    header: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 20px', borderBottom: '1px solid #edebe9', backgroundColor: '#faf9f8',
    },
    headerTitle: { fontSize: '18px', fontWeight: 600, color: '#323130', margin: 0 },
    closeBtn: {
        background: 'none', border: 'none', fontSize: '22px',
        color: '#605e5c', cursor: 'pointer', padding: '2px 6px', lineHeight: 1,
    },
    body: {
        flex: 1,
        overflowY: 'auto',
        padding: '20px',
        display: 'flex', flexDirection: 'column', gap: '4px',
    },
    eventName: {
        fontSize: '16px', fontWeight: 600, color: '#323130', margin: '0 0 12px',
        display: 'flex', alignItems: 'center', gap: '8px',
    },
    colorDot: {
        width: '4px', height: '20px', borderRadius: '2px', flexShrink: 0,
    },
    columns: {
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px',
    },
    field: { marginBottom: '4px' },
    label: { fontSize: '12px', fontWeight: 600, color: '#605e5c', margin: '0 0 2px' },
    value: { fontSize: '14px', color: '#323130', margin: 0 },
    link: { fontSize: '14px', color: '#0078d4', textDecoration: 'underline' },
    separator: { border: 'none', borderTop: '1px solid #edebe9', margin: '8px 0' },
    scheduleList: { listStyle: 'none', padding: 0, margin: 0 },
    scheduleItem: { fontSize: '13px', color: '#323130', padding: '2px 0' },
    footer: {
        display: 'flex', justifyContent: 'flex-end', gap: '8px',
        padding: '12px 20px', borderTop: '1px solid #edebe9', backgroundColor: '#faf9f8',
    },
    btnSecondary: {
        padding: '8px 24px', borderRadius: '4px', fontSize: '14px', fontWeight: 600,
        cursor: 'pointer', border: '1px solid #8a8886', backgroundColor: '#fff', color: '#323130',
    },
    btnPrimary: {
        padding: '8px 24px', borderRadius: '4px', fontSize: '14px', fontWeight: 600,
        cursor: 'pointer', border: '1px solid #0078d4', backgroundColor: '#0078d4', color: '#fff',
    },
};
/* ---- Helpers ---- */
const fmtDate = (dt, pattern = 'dd-MMM-yyyy hh:mm a') => {
    if (!dt)
        return '';
    try {
        const d = typeof dt === 'string' ? new Date(dt) : dt;
        if (isNaN(d.getTime()))
            return String(dt);
        return format(d, pattern);
    }
    catch (_a) {
        return String(dt);
    }
};
const cp = (event, key) => { var _a, _b, _c, _d, _e; return (_e = (_b = (_a = event.customProperties) === null || _a === void 0 ? void 0 : _a[key]) !== null && _b !== void 0 ? _b : (_d = (_c = event.customProperties) === null || _c === void 0 ? void 0 : _c.AdditionalProperties) === null || _d === void 0 ? void 0 : _d[key]) !== null && _e !== void 0 ? _e : ''; };
const cpStr = (event, ...keys) => {
    for (const k of keys) {
        const v = cp(event, k);
        if (v !== undefined && v !== null && v !== '')
            return String(v);
    }
    return '';
};
/** Render a label+value pair, only if value is truthy */
const Field = ({ label, value, isLink, href }) => {
    if (!value && value !== 0)
        return null;
    return (React.createElement("div", { style: s.field },
        React.createElement("div", { style: s.label }, label),
        isLink && href ? (React.createElement("a", { style: s.link, href: href, target: "_blank", rel: "noopener noreferrer" }, value)) : (React.createElement("div", { style: s.value }, value))));
};
/* ---- Event-type specific field renderers ---- */
const ProjectFields = ({ event }) => {
    var _a;
    let esxpBase = '';
    try {
        esxpBase = ((_a = appSettings()) === null || _a === void 0 ? void 0 : _a.esxpBaseUrl) || '';
    }
    catch ( /* */_b) { /* */ }
    const requestId = cpStr(event, 'ResourceRequestId');
    const projectId = cpStr(event, 'ProjectId');
    const demandSourceId = cpStr(event, 'DemandSourceId');
    const isNonContig = !!cp(event, 'isNonContiguousAssignment');
    const schedules = cp(event, 'assignmentSchedules') || [];
    const commentsRaw = cp(event, 'Comments');
    const comments = Array.isArray(commentsRaw) ? commentsRaw.join(' ') : commentsRaw || '';
    return (React.createElement("div", { style: s.columns },
        React.createElement(Field, { label: "Customer Name", value: cpStr(event, 'CustomerName') }),
        React.createElement(Field, { label: "Request Id", value: requestId, isLink: !!requestId && !!projectId, href: `${esxpBase}#/project/${projectId}/resource/${requestId}/false/resourcemanager` }),
        React.createElement(Field, { label: "Requested Start Date", value: fmtDate(cpStr(event, 'RequestedStartDate')) }),
        React.createElement(Field, { label: "Requested End Date", value: fmtDate(cpStr(event, 'RequestedEndDate')) }),
        React.createElement(Field, { label: "Requested Hours", value: cpStr(event, 'RequestedDurationInHours') }),
        isNonContig && React.createElement(Field, { label: "Scheduled Start Date", value: fmtDate(cpStr(event, 'ScheduledStartDate')) }),
        isNonContig && React.createElement(Field, { label: "Scheduled End Date", value: fmtDate(cpStr(event, 'ScheduledEndDate')) }),
        isNonContig && React.createElement(Field, { label: "Total Schedule Hours", value: cpStr(event, 'totalScheduledHours') }),
        React.createElement(Field, { label: "DemandSourceID", value: demandSourceId, isLink: !!demandSourceId, href: `${esxpBase}#/supportdelivery/requestdetails/${demandSourceId}` }),
        React.createElement(Field, { label: "Assignment Status", value: cpStr(event, 'ResourceRequestStatusEnum') }),
        React.createElement(Field, { label: "Request Type", value: cpStr(event, 'RequestType') }),
        React.createElement(Field, { label: "Domain", value: cpStr(event, 'RequestDomain') }),
        React.createElement(Field, { label: "Delivery Location", value: [cpStr(event, 'DeliveryCity'), cpStr(event, 'DeliveryState')].filter(Boolean).join(', ') }),
        React.createElement(Field, { label: "Offering / Product", value: cpStr(event, 'OfferingTypeEnum') }),
        React.createElement(Field, { label: "Requestor Alias", value: cpStr(event, 'Requestor') }),
        React.createElement(Field, { label: "Travel Notes", value: cpStr(event, 'TravelNotes') === 'Blank' ? '' : cpStr(event, 'TravelNotes') }),
        React.createElement(Field, { label: "Delivery Type", value: cpStr(event, 'DeliveryTypeEnum') }),
        React.createElement(Field, { label: "Comments", value: comments }),
        React.createElement(Field, { label: "Created By", value: cpStr(event, 'CreatedBy') }),
        isNonContig && React.createElement(Field, { label: "Non-contiguous Assignment", value: "Yes" }),
        (cp(event, 'IsVBD') === true || cp(event, 'IsVBD') === 'true') &&
            React.createElement(Field, { label: "Accreditation", value: cpStr(event, 'OneProfileProfessionalServiceName') }),
        isNonContig && schedules.length > 0 && (React.createElement("div", { style: Object.assign(Object.assign({}, s.field), { gridColumn: '1 / -1' }) },
            React.createElement("div", { style: s.label }, "Assignment Schedules"),
            React.createElement("ul", { style: s.scheduleList }, schedules.map((sch, i) => (React.createElement("li", { key: i, style: s.scheduleItem },
                fmtDate(sch.forecastDate, 'dd-MMM-yyyy'),
                " \u2014 ",
                sch.durationInHours,
                " hours"))))))));
};
const NonProjectFields = ({ event, userAlias }) => (React.createElement("div", { style: s.columns },
    React.createElement(Field, { label: "User", value: userAlias }),
    React.createElement(Field, { label: "Start Time", value: fmtDate(event.startDtFQ || event.startDt) }),
    React.createElement(Field, { label: "End Time", value: fmtDate(event.endDtFQ || event.endDt) }),
    React.createElement(Field, { label: "Timezone", value: cpStr(event, 'TimeZone') }),
    React.createElement(Field, { label: "Type", value: cpStr(event, 'AssignmentType') || event.eventTypeName || '' }),
    React.createElement(Field, { label: "Comments", value: cpStr(event, 'comments', 'Comments') || event.subject || '' }),
    React.createElement(Field, { label: "Recurrence", value: cp(event, 'IsRecurrent') ? (cpStr(event, 'RecurrencePattern') || 'Yes') : 'None' }),
    React.createElement(Field, { label: "Created By", value: cpStr(event, 'CreatedBy', 'UpdatedBy') || 'system' })));
const OutlookFields = ({ event }) => (React.createElement("div", { style: s.columns },
    React.createElement(Field, { label: "Start Time", value: fmtDate(event.startDtFQ || event.startDt) }),
    React.createElement(Field, { label: "End Time", value: fmtDate(event.endDtFQ || event.endDt) })));
const HolidayFields = ({ event }) => (React.createElement("div", { style: s.columns },
    React.createElement(Field, { label: "Duration in Hours", value: cpStr(event, 'DurationInHours') })));
/* ---- Main component ---- */
export const EventDetailModal = memo(({ isOpen, event, userAlias, onClose, onEdit, onDelete, canEdit = false, }) => {
    var _a;
    const ref = useRef(null);
    useEffect(() => {
        if (!isOpen)
            return;
        const h = (e) => { if (e.key === 'Escape')
            onClose(); };
        document.addEventListener('keydown', h);
        return () => document.removeEventListener('keydown', h);
    }, [isOpen, onClose]);
    useEffect(() => {
        var _a;
        if (isOpen && ref.current) {
            (_a = ref.current.querySelector('button')) === null || _a === void 0 ? void 0 : _a.focus();
            return createFocusTrap(ref.current);
        }
    }, [isOpen]);
    const category = useMemo(() => {
        var _a;
        // If the event is flagged as a public holiday (even when eventCategory is 'nonproject'), use 'publicholiday' for rendering
        if (event === null || event === void 0 ? void 0 : event.isPublicHoliday)
            return 'publicholiday';
        return ((event === null || event === void 0 ? void 0 : event.eventTypeName) || ((_a = event === null || event === void 0 ? void 0 : event.customProperties) === null || _a === void 0 ? void 0 : _a.eventCategory) || '').toLowerCase();
    }, [event]);
    if (!isOpen || !event)
        return null;
    const title = ((_a = event.customProperties) === null || _a === void 0 ? void 0 : _a.ProjectName) || event.name || event.subject || 'Event Details';
    return (React.createElement("div", { style: s.backdrop, onClick: (e) => { if (e.target === e.currentTarget)
            onClose(); } },
        React.createElement("style", null, `@keyframes slideInFromRight { from { transform: translateX(100%); } to { transform: translateX(0); } }`),
        React.createElement("div", { ref: ref, style: s.panel, role: "dialog", "aria-label": `Event Details: ${title}`, "aria-modal": "true" },
            React.createElement("div", { style: s.header },
                React.createElement("h2", { style: s.headerTitle }, "Event Details"),
                React.createElement("button", { style: s.closeBtn, onClick: onClose, "aria-label": "Close" }, "\u00D7")),
            React.createElement("div", { style: s.body },
                React.createElement("div", { style: s.eventName },
                    React.createElement("span", { style: Object.assign(Object.assign({}, s.colorDot), { backgroundColor: event.bgcolor || '#0078d4' }), "aria-hidden": "true" }),
                    title),
                category === 'project' && React.createElement(ProjectFields, { event: event }),
                category === 'nonproject' && React.createElement(NonProjectFields, { event: event, userAlias: userAlias }),
                (category === 'publicholiday' || category === 'msvacation') && React.createElement(HolidayFields, { event: event }),
                (category === 'outlook' || (!category && !event.customProperties)) && React.createElement(OutlookFields, { event: event })),
            React.createElement("div", { style: s.footer },
                canEdit && (React.createElement("button", { style: s.btnPrimary, onClick: () => onEdit === null || onEdit === void 0 ? void 0 : onEdit(event), "aria-label": `Edit event: ${title}` }, "EDIT EVENT")),
                canEdit && (React.createElement("button", { style: Object.assign(Object.assign({}, s.btnSecondary), { color: '#a80000', borderColor: '#a80000' }), onClick: () => onDelete === null || onDelete === void 0 ? void 0 : onDelete(event), "aria-label": `Delete event: ${title}` }, "DELETE EVENT")),
                React.createElement("button", { style: canEdit ? s.btnSecondary : s.btnPrimary, onClick: onClose, "aria-label": "Close event details" }, "CLOSE")))));
});
EventDetailModal.displayName = 'EventDetailModal';
export default EventDetailModal;
//# sourceMappingURL=EventDetailModal.js.map